import assert from 'assert';
import {ScaleLinear} from 'd3';
import * as d3 from 'd3';

import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  BiointelligentLinkAnnotation,
  BiointelligentLinkHighlightRegion,
  PART_ANNOTATION_COLOR,
  PART_ANNOTATION_OUT_OF_SYNC_COLOR,
  LEGACY_PART_HIGHLIGHT_OUT_OF_SYNC_COLOR,
  LINK_HIGHLIGHT_COLOR,
  LINK_HIGHLIGHT_FILL_OPACITY,
  LINK_HIGHLIGHT_HOVER_COLOR,
  LINK_TO_PROTEIN_ANNOTATION_PADDING,
  LINK_TO_PROTEIN_ARROW_CIRCLE_RADIUS,
  LINK_TO_PROTEIN_ARROW_TIP_HEIGHT,
  LINK_TO_PROTEIN_DOT_SPACING,
  LINKED_ENTITY_CHIP_HORIZONTAL_PADDING,
  LINKED_ENTITY_CHIP_ICON_SIZE,
  PART_ANNOTATION_ARCHIVED_COLOR,
  ViewBiointelligentLinkData,
} from 'molbio/maps/BiointelligentLinksConstants';
import {formatAnnotationDisplayName} from 'molbio/maps/linear/AnnotationsHelpers';
import {PartHighlightRegion} from 'molbio/maps/translateLinksToHighlightRegions';
import Type from 'molbio/maps/Type';
import * as sequtil from 'molbio/SeqUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Parameters for drawing an arrow line for the visualization of a Biointelligent Link. These are intended to
 * be passed through as props into an SVG line element.
 */
interface LineParams {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

/**
 * Parameters for drawing a dot for the visualization of a Biointelligent Link to a protein. These are
 * intended to be passed through as props into an SVG circle element.
 */
interface ProteinCircleParams {
  cx: number;
  cy: number;
}

/**
 * Parameters for rendering the display name of the component polymer in the visualization a Biointelligent
 * Link.
 */
interface DisplayNameParams {
  /*
   * The text to render. This may be ellipsized from the actual name of the polymer. If this value is empty
   * string, there is not enough space to render the display name and it should be omitted.
   */
  displayName: string;
  iconId: BenchlingIconName;
  /*
   * The width in pixels that the display name 'chip' will take up.
   */
  width: number;
}

interface LinkToProteinPathParams {
  /*
   * The path of the tip of the arrow in the visualization of a Biointelligent Link to a protein. This is
   * intended to be passed through as a prop to an SVG path element.
   */
  arrowPointD: string | null;
  circleParams: Array<ProteinCircleParams>;
  displayNameParams: DisplayNameParams;
  lineParams: LineParams;
}

/**
 * Get all the parameters necessary for drawing a link to a protein in the linear / sequence map.
 * @param end The index in base pairs of the end of the link
 * @param height The height in pixels of the link visualization
 * @param proteinName The name of the protein being linked to
 * @param sliceEnd The index in base pairs of the end of the sequence map slice.
 * @param type A Type object, the Type for rendering the protein name
 * @param width The width in pixels of the link visualization
 */
export function getLinkToProteinPathParams({
  end,
  height,
  proteinName,
  sliceEnd,
  type,
  width,
}: {
  end: number;
  height: number;
  proteinName: string | null;
  sliceEnd: number;
  type: Type;
  width: number;
}): LinkToProteinPathParams {
  // If the arrow would wrap onto the next slice, do not show the arrow point.
  let arrowPointD: string | null = null;
  const wrapsRight = end > sliceEnd;
  // TODO: Refactor; handle arrows for other sorts of links that care about strand
  if (!wrapsRight) {
    const arrowPointTop = `M ${width - LINK_TO_PROTEIN_DOT_SPACING} ${height / 2 + LINK_TO_PROTEIN_ARROW_TIP_HEIGHT / 2} `;
    const arrowPointMiddle = `L ${width - LINK_TO_PROTEIN_DOT_SPACING / 2} ${height / 2} `;
    const arrowPointBottom = `L ${width - LINK_TO_PROTEIN_DOT_SPACING} ${height / 2 - LINK_TO_PROTEIN_ARROW_TIP_HEIGHT / 2}`;
    arrowPointD = arrowPointTop + arrowPointMiddle + arrowPointBottom;
  }

  // Don't overlap the arrow with circles.
  const circleCxs: Array<number> = [];
  // Don't allow circles closer to the left edge than the 'padding' of the rect.
  const firstCircleCx = LINK_TO_PROTEIN_ARROW_CIRCLE_RADIUS + LINK_TO_PROTEIN_ANNOTATION_PADDING;
  // Start from one circle's worth away from the edge of the arrow and work left. In most cases, the last few
  // circles will be overlapped by the chip.
  const rightOffset = wrapsRight ? LINK_TO_PROTEIN_DOT_SPACING : LINK_TO_PROTEIN_DOT_SPACING * 2;
  let dotCx = width - rightOffset;
  while (dotCx >= firstCircleCx) {
    circleCxs.push(dotCx);
    dotCx -= LINK_TO_PROTEIN_DOT_SPACING;
  }
  const circleParams: Array<ProteinCircleParams> = [];
  for (const cx of circleCxs) {
    const circleParam = {
      cx,
      cy: height / 2,
    };
    circleParams.push(circleParam);
  }

  // If we have a null `ProteinNameAndIcon`, the sequence is inaccessible.
  let displayName = proteinName ?? 'Inaccessible Protein';
  const displayNameWidth = type.width(displayName.length);
  // Available width for the actual text of the display name. We want to show three dots, and the arrow, if it
  // exists. We also need to allocate space for the padding of the rect and the chip, and for the schema icon.
  const constantChipWidth =
    2 * LINK_TO_PROTEIN_ANNOTATION_PADDING +
    // 3x instead of 2x is because we reuse this padding to separate the icon and the text
    3 * LINKED_ENTITY_CHIP_HORIZONTAL_PADDING +
    LINKED_ENTITY_CHIP_ICON_SIZE;
  let availableWidth: number = width - 3 * LINK_TO_PROTEIN_DOT_SPACING - constantChipWidth;
  // If we're not wrapping, we need to make a bit of extra space for the arrow.
  if (!wrapsRight) {
    availableWidth -= LINK_TO_PROTEIN_DOT_SPACING;
  }
  const spaceInside = displayNameWidth < availableWidth;
  if (!spaceInside) {
    const numAvailableChars = availableWidth / type.charWidth();
    displayName = formatAnnotationDisplayName(displayName, numAvailableChars);
  }
  const displayNameForeignObjectWidth =
    // The expected width of the text of the entity name...
    type.width(displayName.length) +
    // ...plus the left & right padding, plus the spacing between the icon and the text...
    3 * LINKED_ENTITY_CHIP_HORIZONTAL_PADDING +
    // ...plus the width of the icon.
    LINKED_ENTITY_CHIP_ICON_SIZE;
  const displayNameParams = {
    displayName,
    iconId: 'protein' as const,
    width: displayNameForeignObjectWidth,
  };

  // If there is a visible arrow, go all the way to the arrow; otherwise, go all the way to the edge of the
  // annotation
  const lineParams: LineParams = {
    x1: LINK_TO_PROTEIN_ANNOTATION_PADDING,
    x2: wrapsRight ? width - LINK_TO_PROTEIN_ANNOTATION_PADDING : width - LINK_TO_PROTEIN_DOT_SPACING,
    y1: height / 2,
    y2: height / 2,
  };

  return {
    arrowPointD,
    circleParams,
    displayNameParams,
    lineParams,
  };
}

export function isPartLinkType(linkType: BiointelligentLinkType): boolean {
  return [
    BiointelligentLinkType.RNA_PART,
    BiointelligentLinkType.DNA_PART,
    BiointelligentLinkType.AMINO_ACID_PART,
  ].includes(linkType);
}

export function isPartLinkRegion(
  linkRegion: BiointelligentLinkHighlightRegion
): linkRegion is PartHighlightRegion {
  return isPartLinkType(linkRegion.linkType);
}

export function getRectangularPath(
  partRegion: BiointelligentLinkHighlightRegion,
  sliceStart: number,
  sliceEnd: number,
  xFn: ScaleLinear<number, number>,
  y: number,
  height: number,
  sequenceLength: number
): string {
  const start = Math.max(sliceStart, partRegion.start);
  const end = Math.min(
    sliceEnd,
    partRegion.start + sequtil.length(partRegion.start, partRegion.end, sequenceLength)
  );

  const xStart = xFn(start);
  const xEnd = xFn(end);
  return `M ${xStart} ${y} L ${xEnd} ${y} L ${xEnd} ${y + height} L ${xStart} ${y + height} Z`;
}

export function getHighlightColor(
  region: BiointelligentLinkHighlightRegion,
  hoveredLinkIndices: {start: number; end: number} | null,
  forLegacyParts: boolean = false
): string {
  if (forLegacyParts) {
    // Legacy parts have a special "out of sync" color that we don't apply for the new parts view.
    assert(
      [BiointelligentLinkType.RNA_PART, BiointelligentLinkType.DNA_PART].includes(region.linkType),
      'Legacy parts only support nucleotide parts'
    );
    return isPartLinkRegion(region) && region.outOfSync
      ? d3.rgb(LEGACY_PART_HIGHLIGHT_OUT_OF_SYNC_COLOR).toString()
      : d3.rgb(LINK_HIGHLIGHT_COLOR).toString();
  }

  const regionIsHovered =
    hoveredLinkIndices != null &&
    sequtil.isRangeWithin(region.start, region.end, hoveredLinkIndices.start, hoveredLinkIndices.end);
  return regionIsHovered
    ? d3.rgb(LINK_HIGHLIGHT_HOVER_COLOR).toString()
    : d3.rgb(LINK_HIGHLIGHT_COLOR).toString();
}

export function getHighlightOpacity(
  region: BiointelligentLinkHighlightRegion,
  useOverlappingPartsStyling: boolean
): number {
  let fillOpacity = LINK_HIGHLIGHT_FILL_OPACITY;
  // For non-overlapping parts, make the shading lighter to indicate archived. For
  // overlapping parts, the part annotation is lighter.
  if (!useOverlappingPartsStyling && isPartLinkRegion(region) && region.isArchived) {
    fillOpacity *= 0.5;
  }
  return fillOpacity;
}

export function getTopLineColor(outOfSync: boolean = false): string {
  const highlightColor = outOfSync
    ? d3.rgb(PART_ANNOTATION_OUT_OF_SYNC_COLOR)
    : d3.rgb(PART_ANNOTATION_COLOR);
  return highlightColor.toString();
}

export function getLinkAnnotationColor(link: BiointelligentLinkAnnotation): string {
  if (!link.isInSync) {
    return PART_ANNOTATION_OUT_OF_SYNC_COLOR;
  } else if (link.isArchived) {
    return PART_ANNOTATION_ARCHIVED_COLOR;
  } else {
    return PART_ANNOTATION_COLOR;
  }
}

function sortLinksByLength(
  links: Array<ViewBiointelligentLinkData>,
  containerSequenceLength: number
): Array<ViewBiointelligentLinkData> {
  return links.toSorted(
    (a, b) =>
      sequtil.length(b.start, b.end, containerSequenceLength) -
      sequtil.length(a.start, a.end, containerSequenceLength)
  );
}

function uniqueLinksForHighlightRegions(
  linkRegions: Array<BiointelligentLinkHighlightRegion>
): Array<ViewBiointelligentLinkData> {
  return Object.values(
    Object.fromEntries(linkRegions.flatMap((region) => region.links).map((l) => [l.id.toString(), l]))
  );
}

/**
 * Calculates how to layer the colored lines bordering the top or bottom of the highlight.
 *
 * Each highlight region has two parts: The shaded background on the sequence map, and thin colored
 * lines that border the top or bottom of the highlight, corresponding to each link in the highlight region.
 * This function calculates which layer each line belongs on.
 * For example, if we have three part links in a slice that all overlap each other, we have three line layers:
 *        ---
 *    ------
 *      ----------
 *    GATTACAGATTACA
 *    TCGGCACTCGGCAC
 *
 * Links are assigned layers based on their type and length - within a type, the longest link goes on the
 * layer closest to the residues.
 */
export function calculateLinkLineLayers(
  linkRegionsInSlice: Array<BiointelligentLinkHighlightRegion>,
  containerSequenceLength: number
): {[linkId: string]: number} {
  const nLayers = linkRegionsInSlice.reduce((max, region) => Math.max(max, region.links.length), 0);
  const sortedLinksInSlice = sortLinksByLength(
    uniqueLinksForHighlightRegions(linkRegionsInSlice),
    containerSequenceLength
  );
  return sortedLinksInSlice.reduce(
    (acc, link, i) => {
      acc[link.id.toString()] = Math.min(i, nLayers - 1);
      return acc;
    },
    {} as {[key: string]: number}
  );
}
